import { Button, Select, Spinner, TextField, Toggle } from '@cimpress/react-components';
import { useContext, useEffect, useState, type CSSProperties } from 'react';
import {
    getAutoLayoutUrl,
    getDocumentUrlFromAgUrl,
    getProductSceneUrl,
    getRenderingUrlDocument,
    getRenderingUrlWithPhotoScene,
} from '../../api';
import { ApiKeyContext } from '../../App';
import { AnchorImage, Content, Control, Page, type AnchorImageProps, type SelectorOption } from '../../Components';
import { ImageListEditor, type ImageAsset } from '../../Components/CompositionCompose/images';
import { useSearchParamsWithApiKey } from '../../tools';
import type { DesignPersonalizationContext } from '../../types/dpcs';
import { getConceptKits } from './data/conceptKit';
import {
    PPAG_LAYOUT_OPTIONS,
    PPAG_TEXT_ONLY_USE_CASE_ID,
    PPAG_USE_CASE_ID,
    TEXT_ONLY_LAYOUT_VALUE,
} from './data/pageLayoutOptions';
import { AUTO_LAYOUT_PRODUCTS } from './data/ppagProducts';

const TEMPLATE_CONTAINER_STYLE: CSSProperties = {
    display: 'grid',
    placeItems: 'center',
    gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
    gap: '24px',
};
const TEMPLATE_ITEM_STYLE: CSSProperties = {
    width: '100%',
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.3)',
};

const IMAGES_LIST: SelectorOption[] = [
    {
        label: 'Select Custom Image',
        value: '',
    },
    {
        label: 'Cimpress',
        value: JSON.stringify({
            previewUrl:
                'https://api.sherbert.cimpress.io/v3/assets/AtC3axDRE4cg37uuBCSuQ~300/webPreview?signature=e3fdfd68a72f546f41a3e0ddef90f39675ffe13e',
            printUrl:
                'https://api.sherbert.cimpress.io/v3/assets/AtC3axDRE4cg37uuBCSuQ~300/print?signature=e3fdfd68a72f546f41a3e0ddef90f39675ffe13e',
            originalSourceUrl:
                'https://api.sherbert.cimpress.io/v3/assets/AtC3axDRE4cg37uuBCSuQ~300?signature=e3fdfd68a72f546f41a3e0ddef90f39675ffe13e',
            height: 15.675,
            width: 20.2,
            purpose: 'logo',
        }),
    },
    {
        label: 'Golden Grove',
        value: JSON.stringify({
            previewUrl:
                'https://api.sherbert.cimpress.io/v3/assets/dhYgHd9EzzLuValaD7dql~300/backgroundRemoved/webPreview?signature=2d40830c9796c257cae2ea96235651f1c2828100',
            printUrl:
                'https://api.sherbert.cimpress.io/v3/assets/dhYgHd9EzzLuValaD7dql~300/backgroundRemoved/print?signature=2d40830c9796c257cae2ea96235651f1c2828100',
            width: 26.9,
            height: 26.9,
            originalSourceUrl:
                'https://api.sherbert.cimpress.io/v3/assets/dhYgHd9EzzLuValaD7dql~300?signature=2d40830c9796c257cae2ea96235651f1c2828100',
            purpose: 'logo',
        }),
    },
    {
        label: 'The Fungi Amongi',
        value: JSON.stringify({
            previewUrl:
                'https://api.sherbert.cimpress.io/v3/assets/tgXdaHmvrebOCfLvrIZgt~200/webPreview?signature=e040423ab95c5efcbe9d681894a2a194dfb68148',
            originalSourceUrl:
                'https://api.sherbert.cimpress.io/v3/assets/tgXdaHmvrebOCfLvrIZgt~200/webPreview?signature=e040423ab95c5efcbe9d681894a2a194dfb68148',
            printUrl:
                'https://api.sherbert.cimpress.io/v3/assets/tgXdaHmvrebOCfLvrIZgt~200/print?signature=e040423ab95c5efcbe9d681894a2a194dfb68148',
            width: 29,
            height: 29,
            purpose: 'logo',
        }),
    },
];

export const PpagGalleryPage = () => {
    const [searchParams, setSearchParams] = useSearchParamsWithApiKey();

    const primaryTextParam = searchParams.get('primaryText') ?? '';
    const secondaryTextParam = searchParams.get('secondaryText') ?? '';
    const imageAssetParam = searchParams.get('imageAsset');
    const layoutIdParam = searchParams.get('layoutId') ?? '';
    const showScenesParam = searchParams.get('showScenes') !== 'false';
    const imageSelectorParam = searchParams.get('imageSelectorParam');

    const [primaryText, setPrimaryText] = useState(primaryTextParam);
    const [secondaryText, setSecondaryText] = useState(secondaryTextParam);
    const [imageAsset, setImageAsset] = useState(
        imageAssetParam ? (JSON.parse(imageAssetParam) as ImageAsset) : undefined,
    );
    const [layoutId, setLayoutId] = useState(
        PPAG_LAYOUT_OPTIONS.find((layout) => layout.value === layoutIdParam) || PPAG_LAYOUT_OPTIONS[0],
    );
    const [showScenes, setShowScenes] = useState<boolean>(showScenesParam);
    const [imageSelector, setImageSelector] = useState(
        IMAGES_LIST.find((option) => option.label === imageSelectorParam) ?? IMAGES_LIST[0],
    );

    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState<AnchorImageProps[]>([]);
    const apiKey = useContext(ApiKeyContext);

    useEffect(() => {
        const searchParamsObj: Record<string, string> = {};
        if (primaryText) searchParamsObj['primaryText'] = primaryText;
        if (secondaryText) searchParamsObj['secondaryText'] = secondaryText;
        if (imageAsset) searchParamsObj['imageAsset'] = JSON.stringify(imageAsset);
        if (layoutId) searchParamsObj['layoutId'] = layoutId.value;
        if (imageSelector) searchParamsObj['imageSelectorParam'] = imageSelector.label;
        if (!showScenes) searchParamsObj['showScenes'] = 'false';

        setSearchParams(searchParamsObj);
    }, [primaryText, secondaryText, imageAsset, layoutId, showScenes, imageSelector, setSearchParams]);

    useEffect(() => {
        if (results.length > 0) {
            generateResults();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to re-run this effect when showScenes changes, not when results changes
    }, [showScenes]);

    const onLayoutChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setLayoutId(selected);
    };

    const onImageSelectorChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setImageSelector(selected);
        const selectedImage = IMAGES_LIST.find((option) => option.value === selected.value);
        const imageAsset = JSON.parse(selectedImage?.value ?? '{}') as ImageAsset;
        console.log({ selectedImage, imageAsset });
        setImageAsset(imageAsset);
    };

    async function generateResults() {
        setIsLoading(true);
        try {
            const conceptKitIds = await getConceptKits();
            const productsForPpag = AUTO_LAYOUT_PRODUCTS.filter((product) =>
                product.useCases.includes('PPAGAutoLayouts'),
            );
            const isTextOnly = layoutId.value === TEXT_ONLY_LAYOUT_VALUE;
            const useCaseId = isTextOnly ? PPAG_TEXT_ONLY_USE_CASE_ID : PPAG_USE_CASE_ID;
            const effectiveLayoutId = isTextOnly ? undefined : layoutId;

            const layouts = await Promise.all(
                productsForPpag.map(async ({ product }, index) => {
                    const conceptKitId = conceptKitIds[index % conceptKitIds.length];
                    const dpcsJson: DesignPersonalizationContext = {
                        designPersonalizationContextVersion: 1,
                        text: [
                            {
                                textField: {
                                    purposeName: 'headline/maintext',
                                    text: primaryText,
                                },
                            },
                            {
                                textField: {
                                    purposeName: 'yourtexthere/additionaltext',
                                    text: secondaryText,
                                },
                            },
                        ],
                        images: imageAsset
                            ? [
                                  {
                                      image: {
                                          originalSourceUrl: imageAsset.originalSourceUrl,
                                          previewUrl: imageAsset.previewUrl,
                                          printUrl: imageAsset.printUrl,
                                          width: isNaN(Number(imageAsset.width)) ? 0 : Number(imageAsset.width),
                                          height: isNaN(Number(imageAsset.height)) ? 0 : Number(imageAsset.height),
                                      },
                                      purpose: imageAsset.purpose ?? 'logo',
                                  },
                              ]
                            : [],
                    };
                    const autoLayoutUrl = getAutoLayoutUrl({
                        conceptKitId,
                        useCaseId,
                        layoutId: effectiveLayoutId?.value,
                        dpcs: [dpcsJson],
                        apiKey,
                        productId: product.productId,
                        productVersion: product.productVersion.toString(),
                        productOptions: product.productOptions,
                    });
                    const transientUrl = await getDocumentUrlFromAgUrl(autoLayoutUrl);
                    const substrateColor = product.productOptions['Substrate Color'];
                    let previewUrl = getRenderingUrlDocument(transientUrl, 0, substrateColor);
                    if (showScenes) {
                        const sceneUrl = await getProductSceneUrl(
                            product.productId,
                            product.productVersion,
                            product.productOptions,
                        );
                        if (sceneUrl) {
                            previewUrl = getRenderingUrlWithPhotoScene(transientUrl, sceneUrl);
                        }
                    }

                    return {
                        documentUrl: autoLayoutUrl,
                        previewUrl,
                        alt: product.productId + conceptKitId,
                        useLoadingTransition: true,
                    };
                }),
            );

            setResults(layouts);
            console.log({ results });
        } catch (error) {
            console.error('Error generating results:', error);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Page>
            <Page.Title>PPAG Gallery</Page.Title>
            <Page.Controls>
                <Control>
                    <Button onClick={generateResults} variant="primary" disabled={false}>
                        Generate Results
                    </Button>
                </Control>
                <Control>
                    <Control.Title>Show scene previews</Control.Title>
                    <Toggle on={showScenes} onClick={() => setShowScenes(!showScenes)} />
                </Control>
                <Control spacing="compact">
                    <Control.Title>Texts</Control.Title>

                    <TextField
                        label="Primary Text"
                        onChange={(event) => setPrimaryText(event.target.value)}
                        value={primaryText ?? ''}
                    />

                    <TextField
                        label="Secondary Text"
                        onChange={(event) => setSecondaryText(event.target.value)}
                        value={secondaryText ?? ''}
                    />
                </Control>
                <Control>
                    <Control.Title>Logo/Image</Control.Title>

                    <Select
                        onChange={onImageSelectorChange}
                        value={imageSelector}
                        options={IMAGES_LIST}
                        label="Image Selector"
                    />
                    {imageSelector.value === '' && (
                        <ImageListEditor
                            initialValue={imageAsset ? [imageAsset] : undefined}
                            onChange={(newValue) => setImageAsset((newValue ?? [])[0])}
                            imageLimit={1}
                        />
                    )}
                </Control>
                <Control>
                    <Control.Title>Layout ID</Control.Title>
                    <Select
                        onChange={onLayoutChange}
                        value={layoutId}
                        options={PPAG_LAYOUT_OPTIONS}
                        label="Layout ID"
                    />
                </Control>
            </Page.Controls>

            <Page.Content>
                {isLoading ? (
                    <Content.Card sticky size="large">
                        <Content.Section>
                            <Spinner />
                        </Content.Section>
                    </Content.Card>
                ) : (
                    <Content.Card size="large">
                        <div style={TEMPLATE_CONTAINER_STYLE}>
                            {results.length
                                ? results.map((image, i) => (
                                      <div key={i}>
                                          <span>{i + 1}</span>
                                          <div key={i} style={TEMPLATE_ITEM_STYLE}>
                                              <AnchorImage {...image} />
                                          </div>
                                      </div>
                                  ))
                                : 'Enter texts and select animage to see results'}
                        </div>
                    </Content.Card>
                )}
            </Page.Content>
        </Page>
    );
};
